import { Box, Typography, Paper, CircularProgress, Alert, TextField } from '@mui/material';
import { Timeline, Schedule, TrendingUp } from '@mui/icons-material';
import type { GPXData } from '../../services/gpxProcessor';
import type { RouteProfile } from '../profileSelector/RouteProfileSelector';
import type { RouteData } from '../../types';

// Import bike profile icons
import TrekkingIcon from '../../assets/bike_profile_icons/BikeProfile_Trekking_light.svg';
import RoadBikeIcon from '../../assets/bike_profile_icons/BikeProfile_RoadBike_light.svg';
import GravelBikeIcon from '../../assets/bike_profile_icons/BikeProfile_GravelBike_light.svg';
import MountainBikeIcon from '../../assets/bike_profile_icons/BikeProfile_MountainBike_light.svg';

interface GPXRoutePreviewProps {
  gpxData: GPXData;
  selectedProfile: RouteProfile;
  calculatedRoute?: RouteData | null;
  routeThumbnail?: string | null;
  isCalculatingRoute?: boolean;
  routeCalculationError?: string;
  routeName: string;
  onRouteNameChange: (name: string) => void;
  onThumbnailClick?: () => void;
  readOnly?: boolean;
}

export default function GPXRoutePreview({ 
  gpxData, 
  selectedProfile,
  calculatedRoute,
  routeThumbnail,
  isCalculatingRoute,
  routeCalculationError,
  routeName,
  onRouteNameChange,
  onThumbnailClick,
  readOnly = false
}: GPXRoutePreviewProps) {
  
  const formatDistance = (meters: number) => {
    if (meters < 1000) return `${meters}m`;
    return `${(meters / 1000).toFixed(1)}km`;
  };

  const formatDuration = (seconds?: number) => {
    if (seconds === undefined) {
      // Fallback to timestamp-based calculation
      if (!gpxData.metadata.timestamps?.start || !gpxData.metadata.timestamps?.end) {
        return 'Unknown';
      }
      
      const start = new Date(gpxData.metadata.timestamps.start);
      const end = new Date(gpxData.metadata.timestamps.end);
      const durationMs = end.getTime() - start.getTime();
      const durationMinutes = Math.round(durationMs / 60000);
      
      if (durationMinutes < 60) return `${durationMinutes}min`;
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      return `${hours}h ${minutes}min`;
    }

    // Use calculated duration
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes}min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}min`;
  };

  const formatElevation = (meters: number) => {
    return `${Math.round(meters)}m`;
  };

  const getProfileDisplayName = (profile: RouteProfile): string => {
    switch (profile) {
      case 'road': return 'Road Bike';
      case 'trekking': return 'Trekking';
      case 'gravel': return 'Gravel';
      case 'mtb': return 'Mountain Bike';
      default: return profile;
    }
  };

  const getBikeProfileIcon = (profile: RouteProfile) => {  
    switch (profile) {
      case 'road':
        return <img src={RoadBikeIcon} alt="Road Bike" style={{ height: 20, width: 20, aspectRatio: 'auto' }} />;
      case 'mtb':
        return <img src={MountainBikeIcon} alt="Mountain Bike" style={{ height: 20, width: 20, aspectRatio: 'auto' }} />;
      case 'gravel':
        return <img src={GravelBikeIcon} alt="Gravel Bike" style={{ height: 20, width: 20, aspectRatio: 'auto' }} />;
      case 'trekking':
        return <img src={TrekkingIcon} alt="Trekking" style={{ height: 20, width: 20, aspectRatio: 'auto' }} />;
      default:
        return <img src={TrekkingIcon} alt="Trekking" style={{ height: 20, width: 20, aspectRatio: 'auto' }} />;
    }
  };

  // Only show route summary when we have calculated data
  const shouldShowRouteSummary = !isCalculatingRoute && calculatedRoute;
  const displayDistance = calculatedRoute?.distance || 0;
  const displayDuration = calculatedRoute?.duration;
  const displayElevation = calculatedRoute?.ascend;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        {readOnly ? 'Saved Route' : 'Route Preview'}
      </Typography>
      
      {/* Route Name Input - only show when not read-only */}
      {!readOnly && (
        <TextField
          label="Route Name"
          value={routeName}
          onChange={(e) => onRouteNameChange(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          placeholder="Enter route name..."
        />
      )}
      
      {routeCalculationError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {routeCalculationError}
        </Alert>
      )}

      {/* Route processing disclaimer - show during calculation and preview, but not in read-only mode */}
      {(isCalculatingRoute || calculatedRoute) && !readOnly && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          <Typography variant="body2">
            Route optimized for cycling - may differ from original GPX track.
          </Typography>
        </Alert>
      )}
      
      {/* Show loading state while calculating */}
      {isCalculatingRoute && (
        <Paper elevation={1} sx={{ p: 1, textAlign: 'center' }}>
          <CircularProgress size={24} sx={{ mb: 1.5 }} />
          <Typography variant="body1" gutterBottom>
            Processing route...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Optimizing {gpxData.metadata.totalPoints} GPS points with {getProfileDisplayName(selectedProfile)} profile
          </Typography>
        </Paper>
      )}
      
      {/* Show route summary only when calculation is complete */}
      {shouldShowRouteSummary && (
        <Paper elevation={1} sx={{ p: 1 }}>
          {/* Route title row with bike icon - mirroring RouteListItem */}
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ width: '100%', mb: 2 }}>
            <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: 0 }}>
              {getBikeProfileIcon(selectedProfile)}
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {routeName}
              </Typography>
            </Box>
          </Box>

          {/* Content row with thumbnail and stats - mirroring RouteListItem */}
          <Box display="flex" alignItems="stretch" gap={2} sx={{ width: '100%' }}>
            {/* Route Thumbnail */}
            {routeThumbnail && (
              <Box sx={{ flexShrink: 0 }}>
                <img 
                  src={routeThumbnail} 
                  alt="Route thumbnail" 
                  onClick={onThumbnailClick}
                  style={{ 
                    width: '80px',
                    height: '60px',
                    objectFit: 'cover', 
                    borderRadius: '6px',
                    border: '1px solid #e0e0e0',
                    cursor: onThumbnailClick ? 'pointer' : 'default'
                  }} 
                />
              </Box>
            )}

            {/* Stats taking full available width - mirroring RouteListItem */}
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="space-evenly"
              sx={{ 
                flex: 1, 
                minWidth: 0,
                gap: 1
              }}
            >
              <Box display="flex" flexDirection="column" alignItems="center" gap={0.5} sx={{ flex: 1 }}>
                <Timeline sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 500 }}>
                  Distance
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.8rem',
                    fontWeight: 500, 
                    color: 'text.primary',
                    textAlign: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {formatDistance(displayDistance)}
                </Typography>
              </Box>
              
              <Box display="flex" flexDirection="column" alignItems="center" gap={0.5} sx={{ flex: 1 }}>
                <TrendingUp sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 500 }}>
                  Elevation
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.8rem',
                    fontWeight: 500, 
                    color: 'text.primary',
                    textAlign: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {displayElevation !== undefined ? formatElevation(displayElevation) : 'N/A'}
                </Typography>
              </Box>
              
              <Box display="flex" flexDirection="column" alignItems="center" gap={0.5} sx={{ flex: 1 }}>
                <Schedule sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 500 }}>
                  Duration
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.8rem',
                    fontWeight: 500, 
                    color: 'text.primary',
                    textAlign: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {formatDuration(displayDuration)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* File info at bottom */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem', mt: 1.5 }}>
            {gpxData.metadata.filename}
            {gpxData.metadata.timestamps?.start && (
              <> • {new Date(gpxData.metadata.timestamps.start).toLocaleDateString()}</>
            )}
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
